import styled from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../../formInputsV2/styles/tokens';

// v2 look for the selectable quote card shown inside the result-page
// selection modals (QuotationCard and its sub-components).
//
// Exports the same names as common/reusable/QuotationCard.styles.js on
// purpose: QuotationCard only had to change its import path, no markup or
// logic. The old file stays untouched because other cards (selected-quote
// cards, Luce) still use it.
//
// Every element inherits Inter from the card, so nothing here needs to name
// the font again except the headings/buttons that the app's global CSS would
// otherwise restyle.

export const QuotationCardStyles = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 20px 24px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
  font-family: ${v2FontFamily};
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(16, 24, 40, 0.09);
  }

  &.selected {
    border-color: ${v2Colors.primary};
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  /* Semestrale-downgrade warning */
  .alert {
    margin: 0 0 12px !important;
    padding: 8px 12px !important;
    border: 1px solid ${v2Colors.warningBorder};
    border-radius: 10px;
    background: ${v2Colors.warningSoft};
    color: ${v2Colors.warningText};
    font-size: 12.5px;
    line-height: 1.4;
  }

  /* Company quality index */
  .badge {
    border-radius: 999px;
    font-family: ${v2FontFamily};
    font-weight: 600;
    letter-spacing: 0;
  }

  .badge .fs-6 {
    font-size: 13px !important;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    gap: 14px;
    padding: 14px;
  }
`;

// Top row: logo + price on the left, "Chiedi all'AI" on the right.
export const LeftCol = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

// Bottom row: company links + quote details on the left, the action on the
// right, separated from the top row by a hairline.
export const RightCol = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  padding-top: 16px;
  border-top: 1px solid ${v2Colors.disabledSurface};

  > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding-top: 14px;
  }
`;

export const LogoPriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px 20px;

  @media (max-width: ${v2Breakpoints.tablet}) {
    gap: 8px 14px;
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 112px;
  height: 64px;
  padding: 8px;
  border: 1px solid ${v2Colors.disabledSurface};
  border-radius: 12px;
  background: ${v2Colors.inputBackground};

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    width: 88px;
    height: 52px;
    padding: 6px;
  }
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px 28px;
`;

export const Price = styled.div`
  h5 {
    margin: 0 0 2px;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.3;
  }

  h6 {
    margin: 0 0 2px;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
  }

  /* price before the discount */
  p {
    margin: 0;
    color: ${v2Colors.textFaint};
    font-family: ${v2FontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    text-decoration: line-through;
  }

  h2 {
    margin: 0;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 28px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    h2 {
      font-size: 24px;
    }
  }
`;

// Document / info links row
export const CompanyInformationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 4px;
`;

export const CompanyInfoElements = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: ${v2Colors.disabledSurface};
  cursor: pointer;
  transition: background-color 150ms ease;

  p {
    margin: 0;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.3;
    text-decoration: none;
    transition: color 150ms ease;
  }

  img {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &:hover {
    background: ${v2Colors.primarySoftHover};

    p {
      color: ${v2Colors.primaryHover};
    }
  }
`;

export const QuoteInformationContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 10px 28px;
  margin-top: 12px;
`;

export const QuoteInformationItem = styled.div`
  min-width: 0;

  h6 {
    margin: 0 0 1px;
    color: ${v2Colors.textMuted};
    font-family: ${v2FontFamily};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
  }

  p {
    margin: 0;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
  }

  p b {
    font-weight: 700;
  }
`;

export const AquistaButtonsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-shrink: 0;
  min-width: 190px;

  @media (max-width: ${v2Breakpoints.tablet}) {
    width: 100%;
    min-width: 0;
  }
`;

export const AquistaButton = styled.button`
  width: 100%;
  padding: 11px 28px;
  border: 1px solid ${v2Colors.primary};
  border-radius: 10px;
  background: ${v2Colors.primary};
  color: #fff;
  font-family: ${v2FontFamily};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  transition: background-color 150ms ease, border-color 150ms ease, transform 100ms ease;

  &:hover {
    border-color: ${v2Colors.primaryHover};
    background: ${v2Colors.primaryHover};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 12px 20px;
  }
`;

// "Aggiunto"
export const SavedButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 28px;
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 10px;
  background: ${v2Colors.primarySoft};
  color: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  cursor: default;

  &::before {
    content: '✓';
  }
`;

// Sits in the top-right of the top row, in the flow (not absolutely
// positioned), so it can never overlap the logo, price or quality badge. On
// small screens only the icon shows; the text stays for screen readers.
export const AiChatButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 10px;
  background: ${v2Colors.primarySoft};
  color: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: background-color 150ms ease;

  &:hover {
    background: ${v2Colors.primarySoftHover};
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${v2Colors.primary};
  }

  span {
    font-weight: 600;
  }

  @media (max-width: 480px) {
    padding: 8px;

    span {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }
  }
`;
